import { getCurrentInstance, onMounted, nextTick } from 'vue'
import { isEmpty, resolve } from '../composables/use-tree'

const EVENT_CODE = {
  up: 'ArrowUp',
  down: 'ArrowDown',
  right: 'ArrowRight',
  left: 'ArrowLeft',
  home: 'Home',
  end: 'End',
  space: 'Space',
  enter: 'Enter'
}

export function useA11y(props, emit, toggleTreeExpansion, activeItem) {
  const instance = getCurrentInstance()
  let treeItems

  const buildA11y = () => {
    treeItems = instance.vnode.el?.querySelectorAll('[role="treeitem"]')
  }

  onMounted(() => buildA11y())

  const resolveTreeItemTarget = (event) =>
    event.currentTarget?.closest?.('[role="treeitem"]') ||
    event.target?.closest?.('[role="treeitem"]') ||
    event.currentTarget

  const ensureTreeItemsReady = () => {
    if (!treeItems || treeItems.length === 0) {
      buildA11y()
    }
  }

  const handleItemKeydown = async (event) => {
    ensureTreeItemsReady()
    const { processedItem, originalEvent } = event
    const target = resolveTreeItemTarget(originalEvent)
    if (!target) return
    const keyCode = originalEvent.code
    let flag = false

    switch (keyCode) {
      case EVENT_CODE.up:
        setFocusToPreviousTreeitem(target)
        flag = true
        break
      case EVENT_CODE.down:
        setFocusToNextTreeitem(target)
        flag = true
        break
      case EVENT_CODE.right:
        if (isExpandable(target)) {
          isExpanded(target)
            ? setFocusToNextTreeitem(target)
            : toggleTreeExpansion(processedItem, true)
        }
        flag = true
        break
      case EVENT_CODE.left:
        if (isExpandable(target) && isExpanded(target)) {
          toggleTreeExpansion(processedItem, false)
          flag = true
        } else if (isInSubtree(target)) {
          setFocusToParentTreeitem(target)
          flag = true
        }
        break
      case EVENT_CODE.home:
        setFocusToTreeitem(getVisibleTreeItems()[0])
        flag = true
        break
      case EVENT_CODE.end:
        setFocusEndTreeitem()
        flag = true
        break
      case EVENT_CODE.space:
      case EVENT_CODE.enter:
        if (processedItem.item?.title) return
        toggleTreeExpansion(processedItem)
        if (resolve(processedItem.item.url) || resolve(processedItem.item.disabled)) {
          flag = false
        } else {
          flag = true
          if (isEmpty(processedItem.items)) {
            resolve(processedItem.item.command, { originalEvent, item: processedItem.item })
            await nextTick()
            if (props.autoActive) activeItem.value = processedItem
            emit('select', event)
          }
        }
    }

    if (flag) {
      originalEvent.stopPropagation()
      originalEvent.preventDefault()
    }
  }

  const getVisibleTreeItems = () => {
    if (!treeItems) return []
    return Array.from(treeItems).filter(isVisible)
  }

  const isMenuVisible = (el) => {
    const style = window.getComputedStyle(el)
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      el.offsetParent !== null &&
      el.clientHeight > 0
    )
  }

  const isVisible = (treeitem) => {
    if (treeitem.getAttribute('aria-hidden') === 'true' || !isMenuVisible(treeitem)) return false
    if (isInSubtree(treeitem)) {
      const parent = getParentTreeitem(treeitem)
      if (!parent || parent.getAttribute('aria-expanded') === 'false') return false
    }
    return true
  }

  const isInSubtree = (treeitem) =>
    treeitem.parentNode?.parentNode?.getAttribute('role') === 'group'

  const getParentTreeitem = (treeitem) => {
    let node = treeitem.parentNode
    if (node) {
      node = node.parentNode
      if (node) {
        node = node.previousElementSibling
        if (node?.getAttribute('role') === 'treeitem') return node
      }
    }
    return null
  }

  const isExpandable = (treeitem) => treeitem.hasAttribute('aria-expanded')
  const isExpanded = (treeitem) => treeitem.getAttribute('aria-expanded') === 'true'

  const setFocusToParentTreeitem = (treeitem) => {
    if (isInSubtree(treeitem)) {
      setFocusToTreeitem(treeitem.parentNode.parentNode.previousElementSibling)
    }
  }

  const setFocusToTreeitem = (treeitem) => treeitem?.focus()

  const setFocusEndTreeitem = () => {
    const visible = getVisibleTreeItems()
    setFocusToTreeitem(visible[visible.length - 1])
  }

  const setFocusToPreviousTreeitem = (treeitem) => {
    const visible = getVisibleTreeItems()
    let prev
    for (let i = 0; i < visible.length; i++) {
      if (visible[i] === treeitem) break
      prev = visible[i]
    }
    if (prev) setFocusToTreeitem(prev)
  }

  const setFocusToNextTreeitem = (treeitem) => {
    const visible = getVisibleTreeItems()
    let next
    for (let i = visible.length - 1; i >= 0; i--) {
      if (visible[i] === treeitem) break
      next = visible[i]
    }
    if (next) setFocusToTreeitem(next)
  }

  return { buildA11y, handleItemKeydown }
}
